import {
  findingsQueryParams,
  getIconClassName,
  resourceTypes,
} from "../../Findings/FindingsUtils";
import React, { useCallback, useContext, useEffect } from "react";
import { ScopesContext, ScopesStateType } from "./ScopeProvider/ScopeProvider";
import { ScopeManagementContext } from "../../Findings/FindingsFilters/ScopeHierarchy/ScopeManagement/ScopeManagementContext";
import {
  LS_SCOPE,
  UNSAVED_SCOPE,
} from "../../Findings/FindingsFilters/FilterUtils";
import useFindingsQueryParam from "../../Findings/FindingsData/useFindingsQueryParam";
import "./ScopeManagement.scss";
import { useLocation } from "react-router-dom";
import useScopesData from "./useScopesData";
import ScopeManagementBar from "./ScopeManagementBar/ScopeManagementBar";
import SelectedScopeManagement from "./SelectedScopeManagement/SelectedScopeManagement";
import ClassNames from "classnames";
import "../FilterManagement/FilterPanel/FilterPanel.scss";
import "../FilterManagement/FiltersMenu/FiltersMenu.scss";
import {
  AppPreferencesContext,
  userActionsEnum,
} from "../../General/AppPreferencesProvider";
import { AppPreferencesContextType } from "../../types/AppPreferencesProvider";
import Chip from "../../Components/Chip/Chip";
import { FieldViewBlock } from "@SeemplicityDev/shared";

interface ScopeManagementProps {
  editable?: boolean;
  withTitle?: boolean;
  // Accepted from callers (e.g. AddToScope) for parity; ScopeManagement builds
  // its own wrapper class internally and does not read `className` at runtime.
  className?: string;
  readOnly?: boolean;
  disabled?: boolean;
  selectedScope?: any;
  displayScopeManagementPanel?: boolean;
  showAsLabel?: boolean;
  displayAsInput?: boolean;
  wrapperClassName?: string;
  selectableNodeType?: string;
  showHeader?: boolean;
  locationContext?: string;
  useSameWidth?: boolean;
  withUnscoped?: boolean;
  viewMode?: boolean;
  // Set to true in contexts that do not have an Apply button (e.g. Rules /
  // Automation Wizard / Remediation Queue scope inputs), where Save/Save As
  // must remain enabled even when pending unapplied changes exist.
  allowSaveWithPendingChanges?: boolean;
  // Passed by some callers (e.g. FilterAndScopeInputs) for parity with
  // FilterManagement; ScopeManagement reads `setDefaultsForScopeAndFilter`
  // from ScopeManagementContext at runtime, not from props. Declared here so
  // the (runtime-inert) prop pass-through type-checks without behavior change.
  setDefaultsForScopeAndFilter?: boolean;
}

const ScopeManagement: React.FC<ScopeManagementProps> = ({
  editable,
  withTitle,
  readOnly,
  disabled,
  selectedScope,
  displayScopeManagementPanel,
  showAsLabel,
  displayAsInput,
  wrapperClassName,
  selectableNodeType,
  showHeader,
  locationContext,
  useSameWidth,
  withUnscoped = false,
  viewMode = false,
  allowSaveWithPendingChanges,
}) => {
  const { isAllowed } = useContext(
    AppPreferencesContext
  ) as AppPreferencesContextType;

  const {
    scopesState: {
      scopesList,
      defaultScope,
      selectedScope: selectedScopeFromState,
    },
    updateScopeState,
    getScopeValueById,
  } = useContext(ScopesContext);

  const { search } = useLocation();

  const { setDefaultsForScopeAndFilter } = useContext(ScopeManagementContext);
  const { param: selectedScopeIdFromURL } = useFindingsQueryParam(
    findingsQueryParams.SCOPE_ID
  );
  const { param: scopeToExecuteFromURL, clearQueryParam } =
    useFindingsQueryParam(findingsQueryParams.SCOPE_TO_EXECUTE);

  useEffect(() => {
    if (!!selectedScope) {
      updateScopeState({ selectedScope });
    }
  }, [selectedScope]);

  // One gate for both the fetched hierarchy and the `unscoped` node in state.
  const showUnscoped = !!(
    withUnscoped && isAllowed(userActionsEnum.VIEW_UNSCOPED_RESOURCES)
  );

  const {
    scopesList: scopesTuple,
    isLoadingScopesData,
    hasScopesError,
  } = useScopesData({ withUnscoped: showUnscoped });

  const getScopes = useCallback(() => {
    if (!scopesTuple) {
      return;
    }

    const [scopes, SCOPE_ALL] = scopesTuple;

    // `unscoped` is the raw scope-hierarchy child node (ScopeNode); the state
    // field is declared as SelectedScope but consumers read the runtime node
    // shape (see ScopeAndGroupsListManagementPopover). Cast preserves runtime.
    const unscoped = (showUnscoped
      ? scopes?.[0]?.children?.find(
          (child) => child?.type === resourceTypes.UNSCOPED
        )
      : null) as unknown as ScopesStateType["unscoped"];

    return {
      scopesList: scopes,
      defaultScope: SCOPE_ALL,
      unscoped,
    };
  }, [scopesTuple, showUnscoped]);

  const setFiltersFromURL = () => {
    // useFindingsQueryParam types `param` as string | string[] | null; this
    // query param is always a single value at runtime.
    const scopeExists = getScopeValueById(
      (selectedScopeIdFromURL as string) || ""
    );
    const selectedScope = scopeExists ? scopeExists : defaultScope;

    updateScopeState({
      selectedScope: selectedScope,
      scopeToExecute: selectedScope?.value?.query,
    });
  };

  const setFiltersFromLocalStorage = () => {
    const lsScope = JSON.parse(localStorage.getItem(LS_SCOPE) || "{}");
    const scopeExists = getScopeValueById(lsScope?.value?.id || "");
    const selectedScope =
      scopeExists || (!!lsScope && !lsScope?.value?.id)
        ? {
            ...lsScope,
            level: lsScope.level,
            parentId: lsScope.parentId,
            isNewScope: !lsScope?.value?.id,
          }
        : defaultScope;

    updateScopeState({
      selectedScope: selectedScope,
      scopeToExecute: lsScope?.scopeToExecute ?? selectedScope?.value?.query,
      isNewScope: !lsScope?.value?.id,
    });
    clearQueryParam([findingsQueryParams.SCOPE_TO_EXECUTE]);
  };

  const classNameWrapper = ClassNames("input-wrap-scope", {
    "app-header-scope-wrap": !displayAsInput,
    "input-holder": displayAsInput,
    "is-editable": editable,
    "has-title": withTitle,
    "has-read-only": readOnly,
    "is-disabled": disabled,
    "has-scope-selected": selectedScope,
    "is-edit": displayScopeManagementPanel,
    "is-show-as-label": showAsLabel,
    "no-border-bottom": locationContext === "resources",
  });

  useEffect(() => {
    if (scopesList?.length) {
      if (setDefaultsForScopeAndFilter && localStorage.getItem(LS_SCOPE)) {
        setFiltersFromLocalStorage();
      } else {
        if (setDefaultsForScopeAndFilter) {
          let scope: Partial<ScopesStateType> = {};

          if (!selectedScope && !!defaultScope && !scopeToExecuteFromURL) {
            scope = {
              selectedScope: defaultScope,
              scopeToExecute: defaultScope?.value?.query,
            };
          }
          updateScopeState(scope);
        }
      }
    }
  }, [scopesList, defaultScope]);

  useEffect(() => {
    if (!!scopesList?.length) {
      // set selected scope creating new rmq from a selected scope and filter.
      const params = new URLSearchParams(search);
      const scopesId = params.get("scopesid");
      if (!!scopesId) {
        const selectedScope = getScopeValueById(scopesId);
        updateScopeState({ selectedScope });
      }
    }
  }, [scopesList, search]);

  useEffect(() => {
    if (!!scopesList?.length && !!selectedScopeIdFromURL) {
      setFiltersFromURL();
    }
  }, [selectedScopeIdFromURL, scopesList]);

  useEffect(() => {
    const scopes = getScopes();

    updateScopeState(scopes || {});
  }, [getScopes]);

  useEffect(() => {
    if (!!scopeToExecuteFromURL && scopesList?.length) {
      const scopeObj: Partial<ScopesStateType> = {
        // useFindingsQueryParam types `param` as string | string[] | null; this
        // query param is always a single value at runtime.
        scopeToExecute: scopeToExecuteFromURL as string,
        selectedScope: {
          label: UNSAVED_SCOPE,
          value: {
            query: scopeToExecuteFromURL as string,
            editable: true,
            isDirty: true,
            id: undefined,
            type: "scope",
          },
          level: 1,
        },
        isDirty: true,
        isNewScope: true,
      };
      updateScopeState(scopeObj);
    }
  }, [scopeToExecuteFromURL, scopesList]);

  return !viewMode ? (
    <div data-testid={"scope-management-wrapper"} className={classNameWrapper}>
      <ScopeManagementBar
        useSameWidth={useSameWidth}
        locationContext={locationContext}
        showHeader={showHeader}
        selectableNodeType={selectableNodeType}
        wrapperClassName={wrapperClassName}
        editable={editable}
        disabled={disabled}
        readOnly={readOnly}
        displayAsInput={displayAsInput}
        isLoadingScopesData={isLoadingScopesData}
        showAsLabel={showAsLabel}
      />
      {displayScopeManagementPanel && (
        <>
          <div className="scope-separator" />
          <div
            data-testid={"scope-management-panel"}
            className={`panel-data-container panel-data-container-scopes`}
          >
            <div className="panel-data-wrap">
              <div className="chips-rows-wrap">
                <SelectedScopeManagement
                  isLoadingScopesData={isLoadingScopesData}
                  hasScopesError={hasScopesError}
                  allowSaveWithPendingChanges={allowSaveWithPendingChanges}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  ) : (
    <FieldViewBlock label="Scope" data-testid={`scope-management`}>
      <Chip
        leftIcon={getIconClassName(selectedScopeFromState?.value?.type)}
        text={selectedScopeFromState?.label}
        noHover
        rightIcon=""
        disabled={false}
        small={false}
        square={false}
        noMargin={false}
        empty={false}
        tooltipContent=""
      />
    </FieldViewBlock>
  );
};

export default ScopeManagement;
